import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, Plus, CheckCircle2, Clock, Users, Mail, Phone, 
  MessageSquare, FileText, Eye, ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONSENT_PURPOSES = [
  { id: 'newsletter', label: 'Newsletter', icon: Mail },
  { id: 'promotions', label: 'Promoções', icon: FileText },
  { id: 'events', label: 'Eventos', icon: Activity },
  { id: 'surveys', label: 'Inquéritos', icon: MessageSquare },
  { id: 'product_updates', label: 'Atualizações de Produto', icon: FileText }
];

const CONTACT_TYPES = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Telefone', icon: Phone },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'postal', label: 'Correio Postal', icon: FileText }
];

const MarketingActionsPage = () => {
  const { user } = useOutletContext();
  const [actions, setActions] = useState([]);
  const [consentStats, setConsentStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, action: null });
  const [expandedAction, setExpandedAction] = useState(null);
  const [formData, setFormData] = useState({
    action_name: '',
    action_type: '',
    purpose: '',
    legal_basis: 'consentimento',
    data_categories: '',
    recipients: '',
    retention_period: '',
    storage_location: '',
    security_measures: '',
    target_consent_purposes: [],
    target_contact_types: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [actionsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/marketing-actions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/consents/statistics`, { withCredentials: true })
      ]);
      setActions(actionsRes.data);
      setConsentStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.action_name || !formData.action_type) {
      toast.error('Nome e tipo de ação são obrigatórios');
      return;
    }
    
    if (formData.target_consent_purposes.length === 0) {
      toast.error('Selecione pelo menos uma finalidade');
      return;
    }
    
    if (formData.target_contact_types.length === 0) {
      toast.error('Selecione pelo menos um canal de comunicação');
      return;
    }
    
    try {
      // Map frontend fields to backend schema
      const payload = {
        name: formData.action_name,
        description: formData.purpose || '',
        action_type: formData.action_type,
        channels: formData.target_contact_types,
        purposes: formData.target_consent_purposes,
        target_audience: formData.recipients || null,
        consent_filter: {
          legal_basis: formData.legal_basis,
          data_categories: formData.data_categories ? formData.data_categories.split(',').map(s => s.trim()) : [],
          retention_period: formData.retention_period,
          storage_location: formData.storage_location,
          security_measures: formData.security_measures ? formData.security_measures.split(',').map(s => s.trim()) : []
        }
      };
      
      await axios.post(`${BACKEND_URL}/api/marketing-actions`, payload, { withCredentials: true });
      toast.success('Ação de marketing criada com sucesso');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating action:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar ação');
    }
  };

  const resetForm = () => {
    setFormData({
      action_name: '',
      action_type: '',
      purpose: '',
      legal_basis: 'consentimento',
      data_categories: '',
      recipients: '',
      retention_period: '',
      storage_location: '',
      security_measures: '',
      target_consent_purposes: [],
      target_contact_types: []
    });
  };

  const handleApprove = async (actionId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/marketing-actions/${actionId}/approve`, {}, { withCredentials: true });
      toast.success('Ação aprovada com sucesso');
      fetchData();
    } catch (error) {
      console.error('Error approving action:', error);
      toast.error('Erro ao aprovar ação');
    }
  };

  const handleViewDetails = async (action) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/marketing-actions/${action.action_id}`, { withCredentials: true });
      setDetailsDialog({ open: true, action: response.data });
    } catch (error) {
      console.error('Error fetching action details:', error);
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handlePurposeToggle = (purposeId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        target_consent_purposes: [...prev.target_consent_purposes, purposeId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        target_consent_purposes: prev.target_consent_purposes.filter(p => p !== purposeId)
      }));
    }
  };

  const handleContactTypeToggle = (typeId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        target_contact_types: [...prev.target_contact_types, typeId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        target_contact_types: prev.target_contact_types.filter(t => t !== typeId)
      }));
    }
  };

  const getStatusBadge = (action) => {
    if (action.dpo_approved) {
      return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Aprovado</Badge>;
    }
    return <Badge className="bg-amber-50 text-amber-700 border-amber-200">Pendente Aprovação DPO</Badge>;
  };

  const canApprove = user?.role === 'dpo' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="marketing-actions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ações de Marketing (Art. 30º RGPD)
          </h1>
          <p className="text-slate-600">
            Registo de atividades de tratamento conforme Artigo 30º do RGPD
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-action-button" className="bg-[#0F172A] hover:bg-[#0F172A]/90">
              <Plus className="w-4 h-4 mr-2" />
              Nova Ação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Registar Nova Ação de Marketing</DialogTitle>
              <DialogDescription>
                Documentar atividade de tratamento conforme Artigo 30º do RGPD
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="basic">Informação Básica</TabsTrigger>
                  <TabsTrigger value="targeting">Público Alvo</TabsTrigger>
                  <TabsTrigger value="compliance">Conformidade</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="action_name">Nome da Ação *</Label>
                      <Input
                        id="action_name"
                        data-testid="action-name-input"
                        value={formData.action_name}
                        onChange={(e) => setFormData({...formData, action_name: e.target.value})}
                        required
                        placeholder="Ex: Campanha Newsletter Verão 2025"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="action_type">Tipo de Ação *</Label>
                      <Input
                        id="action_type"
                        data-testid="action-type-input"
                        value={formData.action_type}
                        onChange={(e) => setFormData({...formData, action_type: e.target.value})}
                        required
                        placeholder="Ex: Email Marketing, SMS, Telemarketing"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="purpose">Finalidade do Tratamento *</Label>
                    <Textarea
                      id="purpose"
                      data-testid="purpose-input"
                      value={formData.purpose}
                      onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                      required
                      placeholder="Ex: Promoção de produtos sazonais de verão"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="data_categories">Categorias de Dados (separadas por vírgula) *</Label>
                    <Input
                      id="data_categories"
                      data-testid="data-categories-input"
                      value={formData.data_categories}
                      onChange={(e) => setFormData({...formData, data_categories: e.target.value})}
                      required
                      placeholder="Ex: nome, email, telefone, preferências"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="targeting" className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                    <h4 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Selecionar Público Alvo
                    </h4>
                    <p className="text-sm text-slate-600">
                      Selecione as preferências de consentimento para filtrar os utilizadores que receberão esta ação.
                    </p>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-3 block">Por Finalidade de Consentimento</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {CONSENT_PURPOSES.map(purpose => (
                        <div key={purpose.id} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border">
                          <Checkbox
                            id={`purpose-${purpose.id}`}
                            checked={formData.target_consent_purposes.includes(purpose.id)}
                            onCheckedChange={(checked) => handlePurposeToggle(purpose.id, checked)}
                          />
                          <div className="flex items-center gap-2">
                            <purpose.icon className="w-4 h-4 text-slate-500" />
                            <label htmlFor={`purpose-${purpose.id}`} className="text-sm font-medium cursor-pointer">
                              {purpose.label}
                            </label>
                          </div>
                          {consentStats && (
                            <Badge variant="outline" className="ml-auto">
                              {consentStats.by_purpose[purpose.id] || 0}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-3 block">Por Tipo de Contacto</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {CONTACT_TYPES.map(type => (
                        <div key={type.id} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border">
                          <Checkbox
                            id={`contact-${type.id}`}
                            checked={formData.target_contact_types.includes(type.id)}
                            onCheckedChange={(checked) => handleContactTypeToggle(type.id, checked)}
                          />
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4 text-slate-500" />
                            <label htmlFor={`contact-${type.id}`} className="text-sm font-medium cursor-pointer">
                              {type.label}
                            </label>
                          </div>
                          {consentStats && (
                            <Badge variant="outline" className="ml-auto">
                              {consentStats.by_contact_type[type.id] || 0}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {consentStats && (
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#0F172A]">Total de Consentimentos Ativos</span>
                        <span className="text-2xl font-bold text-emerald-600">{consentStats.total_active}</span>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="compliance" className="space-y-4">
                  <div>
                    <Label htmlFor="legal_basis">Base Legal (Art. 6º) *</Label>
                    <Input
                      id="legal_basis"
                      data-testid="legal-basis-input"
                      value={formData.legal_basis}
                      onChange={(e) => setFormData({...formData, legal_basis: e.target.value})}
                      required
                      placeholder="Ex: consentimento, interesse legítimo"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="recipients">Destinatários (separados por vírgula) *</Label>
                    <Input
                      id="recipients"
                      data-testid="recipients-input"
                      value={formData.recipients}
                      onChange={(e) => setFormData({...formData, recipients: e.target.value})}
                      required
                      placeholder="Ex: Equipa Marketing Interna, SendGrid"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="retention_period">Prazo de Conservação *</Label>
                      <Input
                        id="retention_period"
                        data-testid="retention-period-input"
                        value={formData.retention_period}
                        onChange={(e) => setFormData({...formData, retention_period: e.target.value})}
                        required
                        placeholder="Ex: 2 anos"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="storage_location">Local de Armazenamento *</Label>
                      <Input
                        id="storage_location"
                        data-testid="storage-location-input"
                        value={formData.storage_location}
                        onChange={(e) => setFormData({...formData, storage_location: e.target.value})}
                        required
                        placeholder="Ex: Cloud UE"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="security_measures">Medidas de Segurança (separadas por vírgula) *</Label>
                    <Textarea
                      id="security_measures"
                      data-testid="security-measures-input"
                      value={formData.security_measures}
                      onChange={(e) => setFormData({...formData, security_measures: e.target.value})}
                      required
                      placeholder="Ex: Encriptação AES-256, Controlo de acessos RBAC"
                    />
                  </div>
                </TabsContent>
              </Tabs>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="submit-action-button" type="submit" className="bg-[#10B981] hover:bg-[#10B981]/90">
                  Registar Ação
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Action Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => setDetailsDialog({ open, action: open ? detailsDialog.action : null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Ação de Marketing</DialogTitle>
          </DialogHeader>
          {detailsDialog.action && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">{detailsDialog.action.action_name}</h3>
                {getStatusBadge(detailsDialog.action)}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">Tipo:</span>
                  <p className="font-medium">{detailsDialog.action.action_type}</p>
                </div>
                <div>
                  <span className="text-slate-600">Base Legal:</span>
                  <p className="font-medium">{detailsDialog.action.legal_basis}</p>
                </div>
              </div>

              <div>
                <span className="text-slate-600 text-sm">Finalidade:</span>
                <p className="font-medium">{detailsDialog.action.purpose}</p>
              </div>

              {/* Target Users Section */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Utilizadores Alvo ({detailsDialog.action.target_users_count || 0})
                </h4>
                
                {detailsDialog.action.target_consent_purposes?.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm text-slate-600">Filtrado por finalidades:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {detailsDialog.action.target_consent_purposes.map(p => (
                        <Badge key={p} variant="outline">{CONSENT_PURPOSES.find(cp => cp.id === p)?.label || p}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {detailsDialog.action.target_contact_types?.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm text-slate-600">Filtrado por tipos de contacto:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {detailsDialog.action.target_contact_types.map(t => (
                        <Badge key={t} variant="outline">{CONTACT_TYPES.find(ct => ct.id === t)?.label || t}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {detailsDialog.action.target_user_details?.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Nome</th>
                          <th className="text-left p-2 font-medium">Email</th>
                          <th className="text-left p-2 font-medium">Função</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsDialog.action.target_user_details.map((usr) => (
                          <tr key={usr.user_id} className="border-t">
                            <td className="p-2">{usr.name}</td>
                            <td className="p-2">{usr.email}</td>
                            <td className="p-2">
                              <Badge variant="outline" className="text-xs">{usr.role}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {detailsDialog.action.target_users_count > 100 && (
                      <p className="text-xs text-slate-500 p-2 text-center">
                        Mostrando 100 de {detailsDialog.action.target_users_count} utilizadores
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Nenhum utilizador específico selecionado</p>
                )}
              </div>

              {!detailsDialog.action.dpo_approved && canApprove && (
                <div className="pt-4 border-t">
                  <Button
                    className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                    onClick={() => {
                      handleApprove(detailsDialog.action.action_id);
                      setDetailsDialog({ open: false, action: null });
                    }}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Aprovar Ação
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Actions List */}
      <div className="grid grid-cols-1 gap-4">
        {actions.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Nenhuma ação registada</h3>
              <p className="text-slate-600">Crie a primeira ação de marketing para começar.</p>
            </CardContent>
          </Card>
        ) : (
          actions.map((action) => (
            <Card key={action.action_id} className="border-slate-200" data-testid={`action-card-${action.action_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {action.action_name}
                      {action.target_users_count > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {action.target_users_count} utilizadores
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">{action.action_type}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(action)}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(action)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Detalhes
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Finalidade:</span>
                      <p className="font-medium text-[#0F172A] truncate">{action.purpose}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Base Legal:</span>
                      <p className="font-medium text-[#0F172A]">{action.legal_basis}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Prazo de Conservação:</span>
                      <p className="font-medium text-[#0F172A]">{action.retention_period}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Criada em:</span>
                      <p className="font-medium text-[#0F172A]">
                        {new Date(action.created_at).toLocaleDateString('pt-PT')}
                      </p>
                    </div>
                  </div>

                  {/* Target Groups Summary */}
                  {(action.target_consent_purposes?.length > 0 || action.target_contact_types?.length > 0) && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-600 mb-2">Público alvo filtrado por:</p>
                      <div className="flex flex-wrap gap-2">
                        {action.target_consent_purposes?.map(p => (
                          <Badge key={p} variant="outline" className="text-xs bg-white">
                            {CONSENT_PURPOSES.find(cp => cp.id === p)?.label || p}
                          </Badge>
                        ))}
                        {action.target_contact_types?.map(t => (
                          <Badge key={t} variant="outline" className="text-xs bg-white">
                            {CONTACT_TYPES.find(ct => ct.id === t)?.label || t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {!action.dpo_approved && canApprove && (
                    <div className="pt-4 border-t border-slate-200 flex justify-end">
                      <Button
                        data-testid={`approve-action-${action.action_id}`}
                        className="bg-[#10B981] hover:bg-[#10B981]/90"
                        onClick={() => handleApprove(action.action_id)}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Aprovar Ação
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default MarketingActionsPage;
